import { useRef } from 'react';
import { PlayIcon, SquareIcon, RotateCcwIcon } from 'lucide-react';

type ResourceStatus = 'started' | 'stopped' | 'starting' | 'stopping' | 'missing';

export type ResourceRow = {
  name: string;
  status: ResourceStatus;
  ms?: number;
  tick?: number;
  category?: string;
  manifest?: {
    version?: string;
    author?: string;
    description?: string;
    fx_version?: string;
    games?: string[];
    dependencies?: string[];
  };
};

const DOT_COLOR: Record<ResourceStatus, string> = {
  started:  '#00ff66',
  stopped:  '#334155',
  starting: '#fbbf24',
  stopping: '#f87171',
  missing:  '#ef4444',
};

function tickColor(ms?: number): string {
  if (!ms) return 'var(--sc-muted)';
  if (ms < 5)  return '#00ff66';
  if (ms < 15) return '#fbbf24';
  return '#f87171';
}

const ROW_H = 44;

type Props = {
  rows: ResourceRow[];
  onSelect: (r: ResourceRow) => void;
  onAction: (name: string, action: 'start' | 'stop' | 'restart') => void;
  actionTarget: string | null;
};

export function ResourceTable({ rows, onSelect, onAction, actionTarget }: Props) {
  const parentRef = useRef<HTMLDivElement>(null);
  const totalH = rows.length * ROW_H;

  return (
    <div className="sc-glass rounded-xl overflow-hidden">
      {/* Header */}
      <div
        className="grid text-[9px] font-semibold uppercase tracking-[0.13em] px-4 py-2.5 border-b"
        style={{
          gridTemplateColumns: '20px 1fr 140px 80px 70px 120px',
          borderColor: 'var(--sc-border)',
          color: 'var(--sc-muted)',
          background: 'var(--sc-bg-elevated)',
        }}
      >
        <span />
        <span>Resource</span>
        <span>Category</span>
        <span>Tick</span>
        <span>Version</span>
        <span className="text-right">Actions</span>
      </div>

      {/* Scrollable body */}
      <div
        ref={parentRef}
        className="overflow-y-auto"
        style={{ height: Math.min(totalH, 560), scrollbarWidth: 'thin' }}
      >
        <div style={{ height: totalH, position: 'relative' }}>
          {rows.map((r, i) => {
            const isPending = actionTarget?.startsWith(`${r.name}:`);
            const dotColor = DOT_COLOR[r.status] ?? '#334155';
            return (
              <div
                key={r.name}
                onClick={() => onSelect(r)}
                className="grid items-center px-4 cursor-pointer transition-colors"
                style={{
                  gridTemplateColumns: '20px 1fr 140px 80px 70px 120px',
                  height: ROW_H,
                  borderBottom: '1px solid var(--sc-border)',
                  background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.008)',
                  contentVisibility: 'auto',
                  containIntrinsicSize: `0 ${ROW_H}px`,
                } as React.CSSProperties}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0,255,209,0.03)')}
                onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.008)')}
              >
                {/* Status dot */}
                <span className="flex items-center justify-center">
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{
                      background: dotColor,
                      boxShadow: r.status === 'started' ? `0 0 5px ${dotColor}` : 'none',
                    }}
                  />
                </span>

                {/* Name */}
                <span
                  className="font-mono text-xs font-semibold truncate pr-2"
                  style={{ color: 'var(--sc-text)' }}
                  title={r.name}
                >
                  {r.name}
                </span>

                {/* Category */}
                <span className="font-mono text-[10px] truncate" style={{ color: 'var(--sc-muted)' }}>
                  {r.category ?? '—'}
                </span>

                {/* Tick */}
                <span className="font-mono text-[11px] tabnum" style={{ color: tickColor(r.tick) }}>
                  {r.tick !== undefined ? `${r.tick.toFixed(1)}ms` : '—'}
                </span>

                {/* Version */}
                <span className="font-mono text-[10px]" style={{ color: 'var(--sc-muted)' }}>
                  {r.manifest?.version ?? '—'}
                </span>

                {/* Actions */}
                <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                  {r.status === 'stopped' || r.status === 'missing' ? (
                    <ActionBtn
                      disabled={!!isPending}
                      color="#00ff66"
                      onClick={() => onAction(r.name, 'start')}
                      icon={<PlayIcon className="h-3 w-3" />}
                      title="Start"
                    />
                  ) : (
                    <>
                      <ActionBtn
                        disabled={!!isPending}
                        color="var(--sc-cyan)"
                        onClick={() => onAction(r.name, 'restart')}
                        icon={<RotateCcwIcon className="h-3 w-3" />}
                        title="Restart"
                      />
                      <ActionBtn
                        disabled={!!isPending}
                        color="#f87171"
                        onClick={() => onAction(r.name, 'stop')}
                        icon={<SquareIcon className="h-3 w-3" />}
                        title="Stop"
                      />
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ActionBtn({
  disabled,
  color,
  onClick,
  icon,
  title,
}: {
  disabled: boolean;
  color: string;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
}) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      title={title}
      className="w-7 h-7 rounded-lg flex items-center justify-center transition-all disabled:opacity-40 hover:brightness-125"
      style={{ background: color + '14', color, border: `1px solid ${color}25` }}
    >
      {icon}
    </button>
  );
}
